<script setup>
// --------------------------------- Imports -------------------------------- //
import { cloneDeep } from 'lodash-es';
import GeneralSettingForm from '~/bi/components/filters/bi-global-filters/bi-global-filters-setting-tab/general-form.vue';
import WidgetsMapping from '~/bi/components/filters/bi-global-filters/bi-global-filters-setting-tab/widgets-mapping.vue';
import BIGlobalFiltersSidebar from '~/bi/components/filters/bi-global-filters/bi-global-filters-sidebar.vue';
import { useBiStore } from '~/bi/store/bi.store';
import { hasFilterWithSameName } from '~/bi/utils/bi-filter-helper.utils';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';

// ---------------------------------- Props --------------------------------- //
defineProps({});

// ---------------------------------- Emits --------------------------------- //
const emit = defineEmits(['close', 'save']);

// ---------------------------- Injects/Provides ---------------------------- //

// ----------------------- Variables - Pinia - consts ----------------------- //
const {
  $t,
  $toast,
} = useCommonImports();
const bi_store = useBiStore();

// --------------------- Variables - Pinia - storeToRefs -------------------- //

// ------------------- Variables - Local - consts and lets ------------------ //

// ------------------------ Variables - Local - refs ------------------------ //
const active_tab = ref('general');
const local_copy = cloneDeep(bi_store.get_global_filters);
const global_filters_of_component = ref(local_copy); // This is used to store the global filters of the component and make changes to it without affecting the store directly
provide('global_filters_of_component', global_filters_of_component);
// ---------------------- Variables - Local - reactives --------------------- //

// --------------------------- Computed properties -------------------------- //
const tabs = computed(() => [{
  uid: 'general',
  label: $t('General'),
  disabled: false,
}, {
  uid: 'widgets',
  label: $t('Widgets'),
  disabled: false,
}]);

const filter_index = computed(() => global_filters_of_component.value.findIndex(filter => filter.uid === bi_store.selected_global_filter_uid));
const is_save_disabled = computed(() => global_filters_of_component.value.length > 0 && !(global_filters_of_component.value[filter_index.value]?.general?.filter_name));

// -------------------------------- Functions ------------------------------- //
function onChangeTab(value) {
  active_tab.value = value.uid;
}
function onCancelConfig() {
  emit('close');
}
function onSaveConfig() {
  if (!global_filters_of_component?.value?.length) {
    emit('close');
    bi_store.updateGlobalFilters(global_filters_of_component.value);
    return;
  }

  if (hasFilterWithSameName(global_filters_of_component.value.filter((_data, index) => index !== filter_index.value), global_filters_of_component.value[filter_index.value].general.filter_name)) {
    const translated_text = `${$t('A filter with the same name')} ${global_filters_of_component.value[filter_index.value].general.filter_name} ${$t('already exists')}`;
    $toast({ text: translated_text, type: 'error' });
    return;
  }

  emit('close'); // To trigger the onUnmount hook inside general-form.vue
  bi_store.updateGlobalFilters(global_filters_of_component.value);

  setTimeout(() => {
    bi_store.applyDefaultFilters();
  }, 0);
}
// -------------------------------- Watchers -------------------------------- //

// ----------------------------- Lifecycle Hooks ---------------------------- //
onMounted(async () => {
  bi_store.resetSelectedGlobalFilter(); // Reset the previous selected global filter when the component is mounted

  if (global_filters_of_component.value.length) {
    bi_store.selected_global_filter_uid = global_filters_of_component.value[0].uid;
  }
});
</script>

<template>
  <HawkModalContainer content_class="rounded-lg !w-[1000px]">
    <div>
      <HawkModalHeader @close="emit('close')">
        <template #header>
          <div
            class="flex items-start p-6 border-b border-b-gray-200 justify-between text-lg font-semibold text-gray-800"
          >
            <div class="flex items-start">
              <div class="flex flex-col justify-start">
                {{ $t('Configure filters') }}
              </div>
            </div>
            <div class="flex font-normal items-center justify-center -m-2">
              <div
                class="text-gray-600 rounded-md cursor-pointer flex justify-center items-center p-2 ml-3 hover:bg-gray-50"
                @click="$emit('close')"
              >
                <IconHawkXClose class="w-6 h-6 text-gray-500 hover:text-gray-900" />
              </div>
            </div>
          </div>
        </template>
      </HawkModalHeader>
      <HawkModalContent :is_scroll="false">
        <div class="h-96 flex gap-4">
          <!-- Left side filter list -->
          <BIGlobalFiltersSidebar />
          <!-- Right side filter config -->
          <div class="w-full">
            <template v-if="global_filters_of_component.length">
              <HawkTabs :tabs="tabs" :active_item="active_tab" @tab-click="onChangeTab" />

              <!-- General config -->
              <div v-if="active_tab === 'general'" class="mt-2">
                <GeneralSettingForm :key="bi_store.selected_global_filter_uid" :filter-index="filter_index" @cancel-setting="onCancelConfig" @save-setting="onSaveConfig" />
              </div>
              <!-- Widgets config -->
              <div v-if="active_tab === 'widgets'" class="mt-2 h-full">
                <WidgetsMapping :key="bi_store.selected_global_filter_uid" @cancel-setting="onCancelConfig" @save-setting="onSaveConfig" />
              </div>
            </template>
            <template v-else>
              <p class="text-center text-gray-500 mt-36">
                No filters available. Please add a filter.
              </p>
            </template>
          </div>
        </div>
      </HawkModalContent>
      <!-- Footer -->
      <HawkModalFooter>
        <template #right>
          <div class="flex justify-end w-full col-span-full">
            <HawkButton
              class="mr-3"
              type="outlined"
              @click="onCancelConfig"
            >
              {{ $t('Cancel') }}
            </HawkButton>
            <HawkButton
              color="primary"
              :disabled="is_save_disabled"
              @click="onSaveConfig"
            >
              {{ $t('Save') }}
            </HawkButton>
          </div>
        </template>
      </HawkModalFooter>
    </div>
  </HawkModalContainer>
</template>
